import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import Player from '@vimeo/player';
import { LIGHTGALLERY_LICENSE_KEY } from '../utils/lightgallery.js';
window.Vimeo = { Player };

const SELECTORS = {
    block: '.wp-block-ssm-image-gallery',
    blockContainer: '.ssm-image-gallery-inner',
    galleryItem: '.gallery-item',
    galleryColumn: '.gallery-column',
    marqueeContent: '.marquee-content',
};

const ROOT_CONTAINER_SELECTOR = '.is-root-container';

const LAYOUTS = {
    SCROLLING_GRID: 'is-layout-scrolling-grid',
    TIGHT_GRID: 'is-layout-tight-grid',
};

const VIDEO_PATTERNS = {
    youtube: [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/,
        /youtube\.com\/shorts\/([^&\s]+)/,
    ],
    vimeo: /vimeo\.com\/(\d+)/i,
};

const VIDEO_MIME_TYPES = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
    m4v: 'video/x-m4v',
    avi: 'video/x-msvideo',
};

const GALLERY_CONFIG = {
    licenseKey: LIGHTGALLERY_LICENSE_KEY,
    controls: true,
    download: false,
    counter: false,
    closable: true,
    showCloseIcon: true,
    showMaximizeIcon: false,
    appendSubHtmlTo: '.lg-sub-html',
    thumbnail: true,
    hideControlOnEnd: false,
    hideBarsDelay: 0,
    mode: 'lg-fade',
    addClass: 'lg-background-mode',
    preload: 2,
    autoplayVideoOnSlide: true,
    gotoNextSlideOnVideoEnd: false,
    videoMaxWidth: '100%',
    youTubePlayerParams: {
        modestbranding: 1,
        showinfo: 0,
        rel: 0,
        controls: 0,
        autoplay: 1,
        mute: 1,
        loop: 1,
        enablejsapi: 1,
    },
    plugins: [
        lgVideo,
        lgThumbnail,
    ],
};

const DEFAULT_DIMENSIONS = {
    thumbnailWidth: 300,
    videoAspectRatio: 16 / 9,
};

/**
 * Initialize all image gallery blocks on the page
 * @returns {ImageGallery[]} Array of initialized gallery instances
 */
export const initImageGallery = () => {
    const blocks = document.querySelectorAll(SELECTORS.block);

    if (!blocks.length) {
        return [];
    }

    const galleries = [];

    blocks.forEach((block) => {
        setupImageGallery(block, galleries);
    });

    return galleries;
};

export const setupImageGallery = (block, galleries = []) => {
    const isEditor = block.closest(ROOT_CONTAINER_SELECTOR);

    try {
        const gallery = new ImageGallery(block, isEditor);
        if (gallery.isInitialized) {
            galleries.push(gallery);
        }

        return galleries;
    } catch (error) {
        console.error('Failed to initialize gallery:', error, block);
    }
};

export class ImageGallery {
    constructor(block) {
        if (!block) {
            throw new Error('Invalid block element');
        }

        this.block = block;
        this.inner = block.querySelector(SELECTORS.blockContainer);

        if (!this.inner) {
            throw new Error('Invalid inner block element');
        }

        this.galleryId = block.getAttribute('data-gallery-id');
        this.lg = null;
        this.isInitialized = false;
        this.clonedNodes = [];

        if (!this.isInitialized) {
            if (!this.galleryId) {
                throw new Error('Missing data-gallery-id attribute');
            }

            this.layout = this.getLayout();
            this.rawData = this.getRawGalleryData();

            if (!this.rawData?.length) {
                throw new Error(`No gallery data found for: ${this.galleryId}`);
            }

            this.buildGalleryHTML();

            this.init();

            this.isInitialized = true;
        }
    }

    getLayout() {
        const { classList } = this.block;

        if (classList.contains(LAYOUTS.SCROLLING_GRID)) {
            return LAYOUTS.SCROLLING_GRID;
        }

        if (classList.contains(LAYOUTS.TIGHT_GRID)) {
            return LAYOUTS.TIGHT_GRID;
        }

        return null;
    }

    getRawGalleryData() {
        const galleryJson = this.block.getAttribute('data-gallery-json');

        if (!galleryJson) {
            console.error(`Gallery data script not found for: ${this.galleryId}`);

            return [];
        }

        try {
            const data = JSON.parse(galleryJson);

            if (!Array.isArray(data)) {
                console.error(`Invalid gallery data format for: ${this.galleryId}`);

                return [];
            }

            return data;
        } catch (error) {
            console.error(`Failed to parse gallery data for: ${this.galleryId}`, error);

            return [];
        }
    }

    buildGalleryHTML() {
        this.cleanupBeforeRebuild();

        switch (this.layout) {
            case LAYOUTS.SCROLLING_GRID:
                this.buildScrollingGrid();
                break;
            case LAYOUTS.TIGHT_GRID:
                this.buildTightGrid();
                break;
            default:
                this.buildDefaultLayout();
        }
    }

    cleanupBeforeRebuild() {
        if (this.lg?.destroy) {
            this.lg.destroy();
            this.lg = null;
        }

        this.clonedNodes.forEach((node) => node.remove());
        this.clonedNodes = [];
        this.inner.innerHTML = '';
    }

    buildDefaultLayout() {
        const fragment = document.createDocumentFragment();

        this.rawData.forEach((item) => {
            const element = item.type === 'video' ? this.createVideoElement(item) : this.createImageElement(item);

            if (element) {
                fragment.appendChild(element);
            }
        });

        this.inner.appendChild(fragment);
    }

    buildScrollingGrid() {
        const columnsCount = 3;
        const columns = Array.from({ length: columnsCount }, () => []);

        // Distribute items across columns
        this.rawData.forEach((item, index) => {
            columns[index % columnsCount].push(item);
        });

        const fragment = document.createDocumentFragment();

        // Build columns
        columns.forEach((columnItems) => {
            const column = document.createElement('div');
            column.classList.add('gallery-column');

            const marqueeContent = document.createElement('div');
            marqueeContent.classList.add('marquee-content');

            columnItems.forEach((item) => {
                const element = item.type === 'video' ? this.createVideoElement(item) : this.createImageElement(item);

                if (element) {
                    marqueeContent.appendChild(element);
                }
            });

            column.appendChild(marqueeContent);
            fragment.appendChild(column);
        });

        this.inner.appendChild(fragment);

        // Handle overflow cloning after DOM insertion
        this.setupScrollingColumns();
    }

    setupScrollingColumns() {
        const containerHeight = this.inner.offsetHeight;

        this.inner.querySelectorAll('.gallery-column').forEach((column) => {
            const marqueeContent = column.querySelector('.marquee-content');
            if (!marqueeContent) {
                return;
            }

            // Get the original items (excluding any clones)
            const getOriginalItems = () => Array.from(marqueeContent.children).filter((child) => !child.classList.contains('is-clone'));

            // Clone items inside marqueeContent until it exceeds container height
            let iterations = 0;
            const maxIterations = 20; // Safety limit to prevent infinite loops

            while (marqueeContent.offsetHeight <= containerHeight && iterations < maxIterations) {
                const originalItems = getOriginalItems();

                if (originalItems.length === 0) {
                    console.warn('No original items found in marquee content');
                    break;
                }

                // Clone all original items and append them to marqueeContent
                originalItems.forEach((item) => {
                    const clone = item.cloneNode(true);
                    clone.classList.add('is-clone');
                    marqueeContent.appendChild(clone);
                    this.clonedNodes.push(clone);
                });

                iterations++;
            }

            // Now that we have enough content height, clone the entire marqueeContent
            // for seamless infinite scrolling
            column.classList.add('is-overflow');
            const marqueeClone = marqueeContent.cloneNode(true);
            marqueeClone.classList.add('is-clone');
            column.appendChild(marqueeClone);
            this.clonedNodes.push(marqueeClone);
        });
    }

    buildTightGrid() {
        const fragment = document.createDocumentFragment();

        this.rawData.forEach((item) => {
            const element = item.type === 'video' ? this.createVideoElement(item) : this.createImageElement(item);

            if (element) {
                fragment.appendChild(element);
            }
        });

        this.inner.appendChild(fragment);
    }

    createImageElement(item) {
        if (!item?.src) {
            return null;
        }

        const a = document.createElement('a');
        a.href = item.src;
        a.classList.add('gallery-item');
        a.setAttribute('aria-label', this.buildAriaLabel(item, 'View image'));

        if (item.srcset) {
            a.setAttribute('data-responsive', item.srcset);
        }

        const subHtml = this.buildSubHtml(item.title, item.caption);
        if (subHtml) {
            a.setAttribute('data-sub-html', subHtml);
        }

        const img = document.createElement('img');
        img.src = item.thumb || item.src;
        img.alt = item.alt || '';
        img.loading = 'lazy';

        if (item.width && item.height) {
            const aspectRatio = item.height / item.width;
            img.width = DEFAULT_DIMENSIONS.thumbnailWidth;
            img.height = Math.round(DEFAULT_DIMENSIONS.thumbnailWidth * aspectRatio);
        }

        a.appendChild(img);

        return a;
    }

    createVideoElement(item) {
        if (!item?.src) {
            return null;
        }

        const a = document.createElement('a');
        a.classList.add('gallery-item');
        a.setAttribute('aria-label', this.buildAriaLabel(item, 'Play video'));

        const videoType = this.detectVideoType(item.src);
        const subHtml = this.buildSubHtml(item.title, item.caption);

        if (subHtml) {
            a.setAttribute('data-sub-html', subHtml);
        }

        const setupMethods = {
            youtube: () => this.setupYouTubeVideo(a, item),
            vimeo: () => this.setupVimeoVideo(a, item),
            direct: () => this.setupDirectVideo(a, item),
        };

        const setupFn = setupMethods[videoType] || setupMethods.direct;
        setupFn();

        return a;
    }

    setupYouTubeVideo(element, item) {
        const videoId = this.getYouTubeId(item.src);

        if (!videoId) {
            console.warn(`Failed to extract YouTube ID from: ${item.src}`);

            return;
        }

        element.setAttribute('data-lg-size', '1280-720');
        element.setAttribute('data-src', `https://www.youtube.com/watch?v=${videoId}`);

        const posterUrl = item.thumb || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        element.setAttribute('data-poster', posterUrl);

        const img = this.createThumbnailImage(posterUrl, item.alt || item.title || 'YouTube video');
        element.appendChild(img);
    }

    setupVimeoVideo(element, item) {
        const videoId = this.getVimeoId(item.src);

        if (!videoId) {
            console.warn(`Failed to extract Vimeo ID from: ${item.src}`);

            return;
        }

        element.setAttribute('data-lg-size', '1280-720');
        element.setAttribute('data-src', `https://vimeo.com/${videoId}`);

        if (item.thumb) {
            element.setAttribute('data-poster', item.thumb);
        }

        const img = this.createThumbnailImage(item.thumb || '', item.alt || item.title || 'Vimeo video');
        element.appendChild(img);
    }

    setupDirectVideo(element, item) {
        element.setAttribute('data-lg-size', '1280-720');

        const videoConfig = {
            source: [
                {
                    src: item.src,
                    type: this.getVideoMimeType(item.src),
                },
            ],
            attributes: {
                preload: 'auto',
                playsinline: true,
                controls: false,
                muted: true,
                autoplay: true,
                loop: true,
            },
        };

        element.setAttribute('data-video', JSON.stringify(videoConfig));

        if (item.thumb) {
            element.setAttribute('data-poster', item.thumb);
        }

        const img = this.createThumbnailImage(item.thumb || '', item.alt || item.title || 'Video');
        element.appendChild(img);
    }

    createThumbnailImage(src, alt) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = alt;
        img.width = DEFAULT_DIMENSIONS.thumbnailWidth;
        img.height = Math.round(DEFAULT_DIMENSIONS.thumbnailWidth / DEFAULT_DIMENSIONS.videoAspectRatio);
        img.loading = 'lazy';

        return img;
    }

    detectVideoType(url) {
        if (!url || typeof url !== 'string') {
            return 'direct';
        }

        for (const pattern of VIDEO_PATTERNS.youtube) {
            if (pattern.test(url)) {
                return 'youtube';
            }
        }

        if (VIDEO_PATTERNS.vimeo.test(url)) {
            return 'vimeo';
        }

        return 'direct';
    }

    getYouTubeId(url) {
        if (!url) return null;

        for (const pattern of VIDEO_PATTERNS.youtube) {
            const match = url.match(pattern);
            if (match?.[1]) {
                return match[1];
            }
        }

        return null;
    }

    getVimeoId(url) {
        if (!url) return null;
        const match = url.match(VIDEO_PATTERNS.vimeo);

        return match?.[1] || null;
    }

    getVideoMimeType(url) {
        if (!url || typeof url !== 'string') {
            return 'video/mp4';
        }

        const extension = url.split('.').pop().toLowerCase().split('?')[0];

        return VIDEO_MIME_TYPES[extension] || 'video/mp4';
    }

    buildAriaLabel(item, fallback) {
        const label = item?.title || item?.caption || item?.alt;

        return label ? label.trim() : fallback;
    }

    buildSubHtml(title, caption) {
        const container = document.createElement('div');

        if (title) {
            const h4 = document.createElement('h4');
            h4.textContent = title;
            container.appendChild(h4);
        }

        if (caption) {
            const p = document.createElement('p');
            p.textContent = caption;
            container.appendChild(p);
        }

        return container.innerHTML;
    }

    init() {
        const galleryItems = this.block.querySelectorAll(SELECTORS.galleryItem);

        if (!galleryItems.length) {
            console.warn(`No gallery items found for: ${this.galleryId}`);

            return;
        }

        requestAnimationFrame(() => {
            try {
                this.lg = lightGallery(this.block, {
                    ...GALLERY_CONFIG,
                    selector: SELECTORS.galleryItem,
                });
            } catch (error) {
                console.error('Failed to initialize lightGallery:', error);
            }
        });
    }

    destroy() {
        if (this.lg?.destroy) {
            this.lg.destroy();
            this.lg = null;
        }

        this.clonedNodes.forEach((node) => node.remove());
        this.clonedNodes = [];
    }
}
